import clsx from 'clsx'
import { memo, useEffect } from 'react'
import { useTwinData } from '../../hooks/storeHooks/useTwin'
import { useDnDFileUpload } from '../../hooks/useDnDFileUpload'
import { AnimatedSpinner } from '../AnimatedSpinner'
import { UploadIcon } from '../Icons'

interface IDnDFileProps {
  disabled?: boolean
  uploadFile: (file: File) => void
  ref?: React.RefObject<HTMLInputElement | null> | null
}

const DnDFile: React.FC<IDnDFileProps> = memo(
  ({ disabled, uploadFile, ref }) => {
    const { addKnowledgeFile, uploadedKnowledge } = useTwinData()

    useEffect(() => {
      if (
        window.webkit &&
        window.webkit.messageHandlers &&
        window.webkit.messageHandlers.appBridge
      ) {
        const payload = {
          action: 'files/filter',
          source: [
            'image/png',
            'image/jpeg',
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'text/plain',
            'text/markdown',
          ],
          allowsMultipleSelection: true,
        }
        window.webkit.messageHandlers.appBridge.postMessage(payload)
      } else {
        console.warn('Native bridge not available (not running in the app?)')
      }
    }, [])

    const generateUniqueFileName = (originalName: string): string => {
      if (!uploadedKnowledge[originalName]) {
        return originalName
      }

      const nameWithoutExt = originalName.substring(
        0,
        originalName.lastIndexOf('.'),
      )
      const extension = originalName.substring(originalName.lastIndexOf('.'))

      let counter = 1
      let newName = `${nameWithoutExt}(${counter})${extension}`

      while (uploadedKnowledge[newName]) {
        counter++
        newName = `${nameWithoutExt}(${counter})${extension}`
      }

      return newName
    }

    const handleFileUpload = (file: File) => {
      const uniqueFileName = generateUniqueFileName(file.name)

      const renamedFile = new File([file], uniqueFileName, { type: file.type })

      addKnowledgeFile({
        id: uniqueFileName,
        fileName: uniqueFileName,
        fileType: file.type,
        fileExtension: file.name.split('.').pop() || '',
        status: 'uploading',
        file: renamedFile,
      })
      uploadFile(renamedFile)
    }

    const {
      value,
      isError,
      isLoading,
      inputRef,
      handleDrop,
      handleInputChange,
      handleClick,
      handleFileError,
    } = useDnDFileUpload({
      disabled,
      onFileSelect: handleFileUpload,
      acceptedTypes: [
        'image/png',
        'image/jpeg',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
        'text/markdown',
      ],
      maxFiles: 10,
      currentFilesCount: Object.keys(uploadedKnowledge).length,
    })

    useEffect(() => {
      if (ref && typeof ref === 'object') {
        ref.current = inputRef.current || null
      }
    }, [inputRef, ref])

    return (
      <>
        <div
          onDrop={handleDrop}
          onDragOver={e => e.preventDefault()}
          className={clsx(
            'p-12 w-full flex h-[247px] flex-col justify-center items-center gap-4 self-stretch group',
            'border-2 border-dashed rounded-2xl',
            'border-white/30 transition-colors duration-200',
            {
              'cursor-pointer hover:border-white/70': !isLoading && !disabled,
              'cursor-not-allowed': isLoading || disabled,
            },
          )}
          onClick={handleClick}
        >
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <AnimatedSpinner />
            </div>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/*, .doc, .docx, .pdf, .txt, .md"
            style={{ display: 'none' }}
            onChange={handleInputChange}
            onError={handleFileError}
            multiple
          />

          <div className="flex flex-col justify-center items-center">
            <div
              className={clsx(
                'btn btn-secondary !w-14 !h-14 flex !rounded-full',
                {
                  'group-hover:!bg-[rgba(128,128,128,0.4)]':
                    !isLoading && !disabled,
                  '!cursor-not-allowed hover:!bg-[rgba(128,128,128,0.3)]':
                    isLoading || disabled,
                },
              )}
            >
              <UploadIcon size={24} />
            </div>
            <div className="text-white text-center text-sm font-normal leading-[150%] max-w-[200px] my-4">
              Drag and drop files here or click to browse
            </div>
            <div className="text-white/50 text-xs font-normal leading-[150%] text-center">
              Supported formats: .doc, .docx, .pdf, .txt, .md, .png and .jpeg
            </div>
          </div>

          {isError && (
            <span className="text-red-500 text-xs mt-2">{isError}</span>
          )}
        </div>
        {value && value}
      </>
    )
  },
)

export { DnDFile }
